import React, { useState, useMemo } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Divider,
    Drawer,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Plus, Shield, X, AlertCircle } from "lucide-react";
import {
    atualizarVinculo,
    ehEquipeBusifarm,
    USER_ACCESS_TYPE_OPTIONS,
    vinculoDaLicenca,
} from "../usersUtils";
import LicenseRoleCard from "./LicenseRoleCard";
import PortalDoClienteSection from "./PortalDoClienteSection";

export default function UserDialog({
    open,
    onClose,
    onSubmit,
    saving,
    editing,
    form,
    setForm,
    references,
    onSalvarPortal,
    salvandoPortal = false,
}) {
    const [selectedLicenseToAdd, setSelectedLicenseToAdd] = useState(null);

    const handleChange = (field) => (event) => {
        const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((current) => ({ ...current, [field]: value }));
    };


    const isGlobalUser = ehEquipeBusifarm(form?.specialAccessType);
    // O identity só aceita a troca para ADMIN ou SUPPORT depois que a conta fica sem vínculo.
    const vinculosAntesDaTroca = isGlobalUser && !ehEquipeBusifarm(editing?.specialAccessType)
        ? (editing?.licenses || []).length
        : 0;
    const noExpiration = form?.expirationDate == null || form?.expirationDate === '';
    const licenses = useMemo(() => form?.licenses || [], [form?.licenses]);
    const roles = form?.roles || [];

    // Available licenses that haven't been added yet
    const availableLicensesToAdd = useMemo(() => {
        const selectedLicenseIds = licenses.map(l => l.id || l.licenseId);
        return (references.licencas || []).filter(
            lic => !selectedLicenseIds.includes(lic.id || lic.licenseId)
        );
    }, [licenses, references.licencas]);

    // Handle adding a new license
    const handleAddLicense = () => {
        if (!selectedLicenseToAdd) return;
        
        setForm((current) => ({
            ...current,
            licenses: [...(current.licenses || []), selectedLicenseToAdd],
        }));
        setSelectedLicenseToAdd(null);
    };

    // Handle removing a license and its associated roles
    const handleRemoveLicense = (license) => {
        const licenseId = license.id || license.licenseId;
        
        setForm((current) => {
            // Remove the license
            const newLicenses = (current.licenses || []).filter(
                l => (l.id || l.licenseId) !== licenseId
            );
            
            // Remove all roles that belong to this license
            const newRoles = (current.roles || []).filter(role => {
                const roleLicenseId = role.licenseId || role.license?.id;
                return roleLicenseId !== licenseId;
            });
            
            return {
                ...current,
                licenses: newLicenses,
                roles: newRoles,
            };
        });
    };

    // Vínculo ainda não gravado nasce Usuário; o administrador do cliente é escolhido no card (BBF-181).
    const papelPadrao = 'USUARIO';

    const handleVinculoChange = (licenseId) => (mudanca) => {
        setForm((current) => ({
            ...current,
            vinculos: atualizarVinculo(current.vinculos || [], licenseId, mudanca, papelPadrao),
        }));
    };

    // Handle role changes for a specific license
    const handleRolesChange = (newRoles) => {
        setForm((current) => ({
            ...current,
            roles: newRoles,
        }));
    };

    // Summary message
    const summaryMessage = isGlobalUser
        ? form?.specialAccessType === 'ADMIN'
            ? 'Usuário global com acesso total ao sistema'
            : 'Usuário global com acesso de suporte/leitura'
        : `${licenses.length} licença(s) | ${roles.length} role(s) vinculada(s)`;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={saving ? undefined : onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 600, md: 720 } } }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
                {/* Header */}
                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #e5e7eb' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {editing ? 'Editar Usuário' : 'Novo Usuário'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {summaryMessage}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} disabled={saving}>
                            <X size={18} />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                    <Stack spacing={3}>
                        {/* User Information Section */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                Informações do Usuário
                            </Typography>
                            <Stack spacing={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Nome de usuário"
                                            value={form.username}
                                            onChange={handleChange('username')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="E-mail"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange('email')}
                                            required
                                        />
                                    </Grid>
                                </Grid>

                                {!editing && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Senha"
                                                type="password"
                                                value={form.password || ''}
                                                onChange={handleChange('password')}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Confirmar Senha"
                                                type="password"
                                                value={form.confirmPassword || ''}
                                                onChange={handleChange('confirmPassword')}
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="DDI"
                                            value={form.ddiPhoneNumber}
                                            onChange={handleChange('ddiPhoneNumber')}
                                        />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Telefone"
                                            value={form.mobilePhoneNumber}
                                            onChange={handleChange('mobilePhoneNumber')}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Access Type Section */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                Tipo de Acesso
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Tipo de acesso"
                                        value={form.specialAccessType}
                                        onChange={handleChange('specialAccessType')}
                                    >
                                        {USER_ACCESS_TYPE_OPTIONS.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                            <Alert severity="info" icon={<Shield size={16} />} sx={{ mt: 2 }}>
                                {isGlobalUser
                                    ? 'ADMIN e SUPPORT são a equipe Busifarm: veem todas as assinaturas e não têm licença, papel nem vínculo.'
                                    : 'Usuários (USER) operam só nas assinaturas em que têm vínculo, com o papel e os perfis de cada uma.'}
                            </Alert>
                            {vinculosAntesDaTroca > 0 && (
                                <Alert severity="warning" icon={<AlertCircle size={16} />} sx={{ mt: 2 }}>
                                    Esta conta tem vínculo com {vinculosAntesDaTroca} assinatura(s). Para mudá-la para {form.specialAccessType},
                                    volte o tipo para USER, remova as licenças e salve; depois troque o tipo.
                                </Alert>
                            )}
                        </Box>

                        <Divider />

                        {/* Account Status Section */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                Status da Conta
                            </Typography>
                            <Stack spacing={2}>
                                <FormControlLabel
                                    control={<Switch checked={Boolean(form.enabled)} onChange={handleChange('enabled')} />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>Usuário ativo</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Usuários inativos não podem fazer login
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    control={<Switch checked={Boolean(form.blocked)} onChange={handleChange('blocked')} />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>Usuário bloqueado</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Bloqueio impede totalmente o acesso ao sistema
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(form.mustRenewPassword)}
                                            onChange={handleChange('mustRenewPassword')}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>Exigir renovação de senha</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Usuário será forçado a alterar a senha no próximo login
                                            </Typography>
                                        </Box>
                                    }
                                />

                                <Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Data de expiração"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={noExpiration ? '' : form.expirationDate || ''}
                                        onChange={handleChange('expirationDate')}
                                        disabled={noExpiration}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={noExpiration}
                                                onChange={(event) =>
                                                    setForm((current) => ({
                                                        ...current,
                                                        expirationDate: event.target.checked ? null : '',
                                                    }))
                                                }
                                            />
                                        }
                                        label="Usuário não expira"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {/* Portal do cliente (BBF-179): só a equipe Busifarm vê, e salva à parte */}
                        <PortalDoClienteSection
                            user={editing}
                            onSalvar={onSalvarPortal}
                            salvando={salvandoPortal}
                            bloqueado={saving}
                        />

                        {/* License & Roles Section - Only for non-global users */}
                        {!isGlobalUser && (
                            <>
                                <Divider />

                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                        Licenças e Roles
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Selecione primeiro as licenças. As roles disponíveis serão exibidas dentro de cada licença.
                                    </Typography>

                                    {/* Add License Section */}
                                    <Box sx={{ mb: 3, p: 2.5, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                                            Adicionar Licença
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={availableLicensesToAdd}
                                                value={selectedLicenseToAdd}
                                                onChange={(_, value) => setSelectedLicenseToAdd(value)}
                                                isOptionEqualToValue={(option, value) =>
                                                    (option?.id || option?.licenseId) === (value?.id || value?.licenseId)
                                                }
                                                getOptionLabel={(option) => {
                                                    const empresa =
                                                        option?.customer?.legalName ||
                                                        option?.empresa?.razao_social ||
                                                        option?.empresa?.nome ||
                                                        'Empresa';
                                                    const plano =
                                                        option?.plan?.productPlanName ||
                                                        option?.plano?.nome_plano ||
                                                        'Plano';
                                                    return `${empresa} - ${plano}`;
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Selecione uma licença" placeholder="Buscar licença..." />
                                                )}
                                                noOptionsText="Todas as licenças já foram adicionadas"
                                            />
                                            <Button
                                                variant="contained"
                                                startIcon={<Plus size={16} />}
                                                onClick={handleAddLicense}
                                                disabled={!selectedLicenseToAdd}
                                                sx={{ 
                                                    whiteSpace: 'nowrap',
                                                    bgcolor: '#0f766e',
                                                    '&:hover': { bgcolor: '#115e59' },
                                                }}
                                            >
                                                Adicionar
                                            </Button>
                                        </Stack>
                                    </Box>

                                    {/* License Cards */}
                                    {licenses.length === 0 ? (
                                        <Alert severity="warning" icon={<AlertCircle size={16} />}>
                                            Nenhuma licença foi vinculada. Adicione ao menos uma licença para definir o escopo de acesso do usuário.
                                        </Alert>
                                    ) : (
                                        <Stack spacing={2}>
                                            {licenses.map((license) => (
                                                <LicenseRoleCard
                                                    key={license.id || license.licenseId}
                                                    license={license}
                                                    availableRoles={references.roles || []}
                                                    selectedRoles={roles}
                                                    onRolesChange={handleRolesChange}
                                                    onRemoveLicense={() => handleRemoveLicense(license)}
                                                    vinculo={vinculoDaLicenca(form?.vinculos || [], license.id || license.licenseId, papelPadrao)}
                                                    onVinculoChange={handleVinculoChange(license.id || license.licenseId)}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            </>
                        )}

                        <Divider />

                        {/* Summary Section */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                Resumo de Acesso
                            </Typography>
                            <Alert severity="success" icon={<Shield size={16} />}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                    {summaryMessage}
                                </Typography>
                                {!isGlobalUser && licenses.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        O usuário terá acesso aos contextos organizacionais definidos pelas licenças selecionadas,
                                        com permissões específicas determinadas pelas roles associadas.
                                    </Typography>
                                )}
                            </Alert>
                        </Box>
                    </Stack>
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" fullWidth onClick={onClose} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={onSubmit} 
                            disabled={saving}
                            sx={{ 
                                bgcolor: '#0f766e',
                                '&:hover': { bgcolor: '#115e59' },
                            }}
                        >
                            {saving ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar usuário'}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
}
